import { WarningCircle } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { patch } from 'src/api/requests';
import Accordion from 'src/components/Accordion';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Textarea } from 'src/components/Textarea';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  Agent,
  getReferencedContextParams,
} from 'src/interfaces/agent.interface';
import { CallContextForm } from 'src/pages/Agents/components/CallContextForm';
import { cn } from 'src/utils/className';

type SimulationTestingSidebarProps = {
  agent: Agent;
};

type ConversationMessage = {
  role: 'assistant' | 'user';
  content: string;
};

export const SimulationTestingSidebar = ({
  agent,
}: SimulationTestingSidebarProps) => {
  const { get, patch, post } = useAuthedRequest();
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [selectedCustomerAgentId, setSelectedCustomerAgentId] =
    useState<string>('');
  const [numTurns, setNumTurns] = useState(2);
  const [isEarlyTermination, setIsEarlyTermination] = useState(false);
  const [originalAgentPrompt, setOriginalAgentPrompt] = useState<string>(
    typeof agent.prompt === 'object' ? agent.prompt.content : agent.prompt,
  );
  const [agentPrompt, setAgentPrompt] = useState<string>(
    typeof agent.prompt === 'object' ? agent.prompt.content : agent.prompt,
  );
  const [contextObject, setContextObject] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (agent) {
      const referencedContextParams = getReferencedContextParams(agent);
      setContextObject(
        referencedContextParams.reduce(
          (acc, param) => {
            acc[param] = '';
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
      setAgentPrompt(
        typeof agent.prompt === 'object' ? agent.prompt.content : agent.prompt,
      );
      setOriginalAgentPrompt(
        typeof agent.prompt === 'object' ? agent.prompt.content : agent.prompt,
      );
    }
  }, [agent]);

  const fetchCustomerAgents = useCallback(
    async (content: string) => {
      const query = new URLSearchParams({
        filters: JSON.stringify({
          ...(content.length !== 0 && { name: content }),
          type: 'agent_customer',
        }),
      });
      const data = await get(`/agents?${query.toString()}`);
      return data.items.map((agent: Agent) => ({
        value: agent.id,
        label: agent.label || agent.name,
      }));
    },
    [get],
  );

  const updateAgentPrompt = useCallback(
    async (prompt: string) => {
      const response = await patch(`/agents/${agent.id}`, {
        prompt: {
          content: prompt,
        },
      });
      setAgentPrompt(response.prompt.content);
      setOriginalAgentPrompt(response.prompt.content);
    },
    [patch, agent],
  );

  const runSimulation = async () => {
    setSimulationLoading(true);
    if (agentPrompt !== originalAgentPrompt) {
      await updateAgentPrompt(agentPrompt);
    }
    const response = await post(`/simulation/run`, {
      bot_agent_id: agent.id,
      customer_agent_id: selectedCustomerAgentId,
      context: contextObject,
      num_turns: numTurns,
    });
    setConversation(response.message_history);
    setIsEarlyTermination(response.terminated_early);
    setSimulationLoading(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h4>Agent Prompt</h4>
        <Textarea
          value={agentPrompt}
          onChange={(e) => setAgentPrompt(e.target.value)}
          rows={6}
          disabled={simulationLoading}
        />
      </div>

      {Object.keys(contextObject).length > 0 && (
        <Accordion title="Context" defaultOpen={true} className="border-none">
          <CallContextForm
            contextObject={contextObject}
            setContextObject={setContextObject}
          />
        </Accordion>
      )}
      <div className="flex gap-2">
        <div className="flex flex-col gap-2 w-1/2">
          <h4>Simulated Customer</h4>
          <AutoCompleteSelectBox
            fetchOptions={fetchCustomerAgents}
            onChange={(value) => setSelectedCustomerAgentId(value)}
            placeholder="Persona"
          />
        </div>
        <div className="flex flex-col gap-2 w-1/2">
          <h4>Number of turns (max 50)</h4>
          <Input
            type="number"
            value={numTurns}
            onChange={(e) => setNumTurns(parseInt(e.target.value))}
            min={1}
            max={50}
          />
        </div>
      </div>
      <Button
        onClick={async () => {
          await runSimulation();
        }}
        disabled={
          simulationLoading ||
          numTurns < 1 ||
          numTurns > 50 ||
          !selectedCustomerAgentId
        }
      >
        {simulationLoading ? (
          <Loading size="16" />
        ) : (
          <>
            {agentPrompt !== originalAgentPrompt
              ? 'Update Agent and Run Simulation'
              : 'Run Simulation'}
          </>
        )}
      </Button>
      {!simulationLoading && (
        <div className="flex-1 flex flex-col">
          {isEarlyTermination && (
            <div className="flex-1 border border-neutral-200 rounded-lg p-4 bg-warning-200 flex items-center justify-center">
              <WarningCircle size={24} className="text-warning" />
              <p className="text-neutral-400">The simulation ended early.</p>
            </div>
          )}
          {!conversation ||
            (conversation.length === 0 && (
              <div className="flex-1 border border-neutral-200 rounded-lg p-4 bg-neutral-50 flex items-center justify-center">
                <p className="text-neutral-400">
                  The simulated conversation will appear here.
                </p>
              </div>
            ))}
          {conversation && conversation.length > 0 && (
            <div className="flex-1 overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50">
              <div className="space-y-4">
                {conversation.map((msg, index) => {
                  const isHuman = msg.role === 'user';

                  return (
                    <div
                      key={index}
                      id={`message-${index}`}
                      className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={cn(
                          `max-w-[80%] rounded-lg px-4 py-2 cursor-pointer transition-opacity ${
                            isHuman
                              ? 'bg-primary text-neutral-0 hover:opacity-80'
                              : 'bg-neutral-0 text-neutral-800 border border-neutral-200 hover:opacity-60'
                          }`,
                        )}
                      >
                        <div className="text-xs opacity-70 mb-1">
                          {msg.role === 'user' ? 'HUMAN' : 'BOT'}
                        </div>
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
